import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowDown, Phone, Mail, CheckCircle2, AlertCircle,
  Zap, Bot, Users, TrendingUp, LayoutDashboard, MessageSquare, Send,
  Repeat, Mails, PhoneCall, Wrench, Sparkles, ClipboardCheck, Search,
  Target, LineChart, Trophy, Building2, Home, ThermometerSun, Droplets,
  Hammer, Plug, Umbrella, Wind, ShieldCheck, Layers, Waves, ChevronDown,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { CONTACT_INFO } from '@/lib/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOUR_STAGES = [
  { n: '01', tag: 'LEAD',     title: 'New opportunity.',     body: 'When a new lead enters VSM, we begin the outreach process. The goal: make contact and create an active sales opportunity.', chips: ['Calls','Texts','Emails','Remarketing'], color: 'bp-blue' },
  { n: '02', tag: 'PROSPECT', title: 'Active sales opportunity.', body: 'Once someone becomes a qualified or engaged prospect, VSM increases the level of follow-up to maintain momentum and help move the prospect toward a buying decision.', chips: ['Calls','Texts','Emails','Remarketing','Direct Mail'], color: 'bp-teal' },
  { n: '03', tag: 'DEAL',     title: 'Customer won.',        body: 'When the prospect becomes a customer, sales nurture stops. The customer enters our Referral Program to turn one customer into additional customers.', chips: ['Referral Program'], color: 'bp-amber' },
  { n: '04', tag: 'LOST',     title: 'Opportunity closed.',  body: 'When an opportunity is genuinely lost, sales outreach stops. No further action. Simple, clean and measurable.', chips: ['No Further Action'], color: 'bp-slate' },
];

const WHAT_WE_DO = [
  { icon: PhoneCall,     title: 'Calls',             body: 'VSM follows up with leads and active prospects through structured telephone outreach. Human Virtual Assistants take the call where a real conversation is appropriate.' },
  { icon: MessageSquare, title: 'Text Messaging',    body: 'Immediate and ongoing SMS communication keeps opportunities from going cold. Automation handles the routine; humans step in when needed.' },
  { icon: Mails,         title: 'Email Follow-Up',   body: 'Structured email campaigns keep the business in front of leads and prospects throughout the entire buying process.' },
  { icon: Repeat,        title: 'Remarketing',       body: 'Continue showing relevant advertising to leads and prospects while they evaluate their options and compare providers.' },
  { icon: Send,          title: 'Direct Mail',       body: 'Qualified prospects can receive physical mail designed to create another high-trust sales touchpoint the competition rarely delivers.' },
  { icon: Sparkles,      title: 'Referral Management', body: 'Once a customer becomes a DEAL, VSM transitions them into a systematic referral program designed to generate additional opportunities.' },
];

const OPERATING_MODEL = [
  { pill: 'AI',                icon: Bot,           bg: 'from-bp-blue to-[#0a5fd1]', items: ['Monitor activity', 'Draft communications', 'Personalize outreach', 'Summarize interactions', 'Identify priorities', 'Automate routine follow-up'] },
  { pill: 'Automation',        icon: Zap,           bg: 'from-bp-teal to-[#0da78e]', items: ['Triggered emails', 'Triggered texts', 'Reminders', 'Workflow changes', 'CRM activity', 'Routine nurturing'] },
  { pill: 'Virtual Assistants', icon: Users,        bg: 'from-[#F59E0B] to-[#d97706]', items: ['Calls', 'Personal follow-up', 'Questions', 'Appointment coordination', 'Exceptions', 'Prospects requiring human communication'] },
  { pill: 'Client Sales Team', icon: Trophy,        bg: 'from-[#FB6A48] to-[#e94a26]', items: ['Technical selling', 'Pricing', 'Proposals', 'Consultative conversations', 'High-value closing', 'Deep client relationships'] },
];

const WHY_VSM = [
  { icon: ClipboardCheck, title: 'Consistency',   body: 'Follow-up happens whether your internal team remembers or not.' },
  { icon: Zap,            title: 'Speed',         body: 'New opportunities can begin receiving attention immediately.' },
  { icon: ShieldCheck,    title: 'Accountability', body: 'You can see exactly what activity was completed.' },
  { icon: TrendingUp,     title: 'Scalability',   body: 'AI, automation and VAs allow the process to scale without hiring a full internal sales-management department.' },
  { icon: LayoutDashboard,title: 'Visibility',    body: 'See leads, prospects, deals, lost opportunities and activity from one dashboard.' },
  { icon: Trophy,         title: 'Revenue Focus', body: 'The ultimate KPI is not activity. It is signed business.' },
];

const INDUSTRIES = [
  { name: 'Pool Builders',   icon: Waves },
  { name: 'Pool Remodelers', icon: Droplets },
  { name: 'HVAC',            icon: ThermometerSun },
  { name: 'Plumbing',        icon: Wrench },
  { name: 'Roofing',         icon: Home },
  { name: 'Windows & Doors', icon: Building2 },
  { name: 'Remodeling',      icon: Hammer },
  { name: 'Electrical',      icon: Plug },
  { name: 'Outdoor Living',  icon: Umbrella },
  { name: 'Restoration',     icon: Wind },
  { name: 'Other Home-Service Businesses', icon: Layers },
];

const CHALLENGES = [
  "We don't follow up consistently",
  "Leads aren't contacted quickly enough",
  "Estimates aren't being worked",
  'Salespeople need support',
  'We have a large old database',
  'We need appointment-setting help',
  "We don't know our conversion numbers",
  'We want more referrals',
  'We need the entire sales process managed',
  'Other',
];

// ────────────────────────────────────────────────────────────────
// Small presentational subcomponents (kept inline for one-page compile)
// ────────────────────────────────────────────────────────────────

const KPI = ({ label, value, delta, accent = 'text-bp-blue', dataTestid }) => (
  <div className="bg-white rounded-2xl border border-bp-slate-100 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={dataTestid}>
    <div className="text-[10.5px] font-bold uppercase tracking-wider text-bp-slate-soft">{label}</div>
    <div className={`font-display font-black text-[28px] leading-tight mt-1.5 ${accent}`}>{value}</div>
    {delta && <div className="text-[11.5px] font-semibold text-bp-teal mt-1 flex items-center gap-1"><TrendingUp size={12}/> {delta}</div>}
  </div>
);

const ActivityRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-[13px] py-1.5">
    <span className="text-bp-slate">{label}</span>
    <span className="font-mono font-bold text-bp-ink">{value}</span>
  </div>
);

const StickyCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 900);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-30 transition-all duration-300 opacity-100 translate-y-0`} data-testid="vsm-sticky-cta">
      <a
        href="#vsm-form"
        onClick={() => { if (window.gtag) window.gtag('event', 'vsm_sticky_cta_click'); }}
        className="btn-primary shadow-2xl backdrop-blur-md !py-3 !px-6"
      >
        <Phone size={15}/> Schedule a VSM Strategy Call
      </a>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────

export default function VirtualSalesManagementPage() {
  const [form, setForm] = useState({
    firstName: '', lastName: '', company: '', website: '', email: '', phone: '',
    industry: '', monthlyLeadVolume: '', avgJobSize: '', salespeople: '', currentCrm: '',
    existingLeads: '', challenge: '',
  });
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.company) {
      setStatus({ state: 'error', message: 'Please fill in your name, email, and company.' });
      return;
    }
    setStatus({ state: 'loading', message: '' });
    try {
      const messageBody =
        `VSM STRATEGY CALL REQUEST\n\n` +
        `Company:            ${form.company}\n` +
        `Website:            ${form.website || 'n/a'}\n` +
        `Industry:           ${form.industry || 'n/a'}\n` +
        `Monthly Lead Vol:   ${form.monthlyLeadVolume || 'n/a'}\n` +
        `Average Job Size:   ${form.avgJobSize || 'n/a'}\n` +
        `# of Salespeople:   ${form.salespeople || 'n/a'}\n` +
        `Current CRM:        ${form.currentCrm || 'n/a'}\n` +
        `Existing Leads:     ${form.existingLeads || 'n/a'}\n` +
        `Biggest Challenge:  ${form.challenge || 'n/a'}\n\n` +
        `Source: VSM Landing Page`;
      const res = await axios.post(`${API}/contact`, {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        company: form.company,
        service: 'Virtual Sales Management',
        message: messageBody,
      });
      setStatus({ state: 'success', message: res.data?.message || "Thanks — we'll be in touch within one business day to schedule your VSM review." });
      setForm({ firstName:'',lastName:'',company:'',website:'',email:'',phone:'',industry:'',monthlyLeadVolume:'',avgJobSize:'',salespeople:'',currentCrm:'',existingLeads:'',challenge:'' });
      if (window.gtag) window.gtag('event', 'vsm_form_submission');
    } catch {
      setStatus({ state: 'error', message: 'Something went wrong. Please email hello@bp.agency directly.' });
    }
  };

  const trackCTA = (label) => () => { if (window.gtag) window.gtag('event', 'vsm_cta_click', { cta: label }); };

  return (
    <main data-testid="vsm-page" className="bg-white">
      <SEO
        title="Virtual Sales Management for Service Contractors | Brand Partners"
        description="Turn more leads and prospects into signed deals with Virtual Sales Management from Brand Partners. Calls, texts, emails, remarketing, direct mail, referral management and complete sales visibility."
        path="/vsm"
      />

      <StickyCTA />

      {/* ═════════ HERO ═════════ */}
      <section className="relative pt-12 lg:pt-16 pb-16 lg:pb-20 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <Reveal as="div" className="inline-flex items-center gap-2 bg-white border border-bp-blue/20 rounded-full px-3.5 py-1.5 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-bp-teal animate-pulse"/>
              <span className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-bp-blue">Virtual Sales Management</span>
            </Reveal>
            <Reveal as="h1" delay={70} className="h-display mt-5 text-[44px] sm:text-[60px] lg:text-[72px] leading-[1.02]">
              You Already Have Leads.<br/>
              <span className="grad-text">Let&rsquo;s Help You Close More of Them.</span>
            </Reveal>
            <Reveal as="p" delay={140} className="mt-6 text-[17.5px] text-bp-slate leading-relaxed max-w-xl">
              VSM combines technology, AI, automation and real human follow-up to manage your sales opportunities from the moment a lead enters your pipeline through the final sale.
            </Reveal>
            <Reveal as="div" delay={200} className="mt-8 flex flex-wrap gap-3">
              <a href="#vsm-form" onClick={trackCTA('hero-primary')} className="btn-primary" data-testid="vsm-hero-primary-cta">
                Schedule a VSM Strategy Call <ArrowRight size={16}/>
              </a>
              <a href="#how-vsm-works" onClick={trackCTA('hero-secondary')} className="btn-secondary" data-testid="vsm-hero-secondary-cta">
                How VSM Works <ArrowDown size={15}/>
              </a>
            </Reveal>
            <Reveal as="div" delay={240} className="mt-6 text-[13px] text-bp-slate-soft flex items-center gap-2">
              <CheckCircle2 size={14} className="text-bp-teal"/> Built for service businesses where every opportunity matters.
            </Reveal>
          </div>

          {/* ── Hero dashboard mock ── */}
          <Reveal as="div" delay={200} className="lg:col-span-6">
            <div className="rounded-3xl bg-white border border-bp-slate-200 shadow-2xl overflow-hidden" data-testid="vsm-hero-dashboard">
              <div className="flex items-center justify-between px-5 py-3 border-b border-bp-slate-100 bg-bp-slate-50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400"/>
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400"/>
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400"/>
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.15em] font-bold text-bp-slate">VSM Performance</div>
                <div className="text-[10px] text-bp-slate-soft">Last 30 Days</div>
              </div>
              <div className="p-5 grid grid-cols-2 gap-3">
                <KPI label="Signed Deal Value" value="$327,500" delta="↑ 28.4%" accent="grad-text" dataTestid="kpi-signed-value"/>
                <KPI label="Signed Deals" value="11" accent="text-bp-blue" dataTestid="kpi-signed-count"/>
                <KPI label="VSM Activities" value="1,074" accent="text-bp-teal"/>
                <KPI label="Prospects" value="47" accent="text-[#F59E0B]"/>
                <div className="col-span-2 bg-white border border-bp-slate-100 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-bp-slate-soft">Activity Breakdown</div>
                    <div className="text-[10.5px] font-mono text-bp-slate-soft">1,074 total</div>
                  </div>
                  <ActivityRow label="Calls"   value="326"/>
                  <ActivityRow label="Texts"   value="418"/>
                  <ActivityRow label="Emails"  value="287"/>
                  <ActivityRow label="Mailers" value="43"/>
                </div>
                <div className="col-span-2 bg-gradient-to-br from-bp-blue-50 to-white rounded-2xl p-4 flex items-center justify-between border border-bp-blue/20">
                  <div>
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-bp-blue">Referrals</div>
                    <div className="font-display font-black text-[22px] text-bp-ink mt-0.5">8 generated</div>
                  </div>
                  <Sparkles className="text-bp-blue" size={28}/>
                </div>
              </div>
              <div className="px-5 pb-4 text-[10px] text-bp-slate-soft text-center italic">Sample dashboard data.</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════ SECTION 2 — PROBLEM ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 text-center">
          <Reveal as="div" className="kicker justify-center"><span className="dot"/>The gap</Reveal>
          <Reveal as="h2" delay={60} className="h-display mt-3 text-[36px] sm:text-[52px]">What Happens After the <span className="grad-text">Lead Comes In?</span></Reveal>
          <Reveal as="p" delay={120} className="mt-6 text-[17.5px] text-bp-slate leading-relaxed">
            Marketing can generate the opportunity. But generating a lead doesn&rsquo;t guarantee that:
          </Reveal>
          <Reveal as="ul" delay={140} className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-left max-w-xl mx-auto">
            {['Someone calls','Someone texts','Someone follows up','Someone sends the right information','Someone stays in front of the prospect','Someone asks for the sale','Someone asks the customer for a referral'].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-[15px] text-bp-slate"><CheckCircle2 size={17} className="text-bp-blue mt-0.5 flex-shrink-0"/>{t}</li>
            ))}
          </Reveal>
          <Reveal as="p" delay={200} className="mt-10 text-[17px] text-bp-slate max-w-2xl mx-auto">
            Most businesses don&rsquo;t need another CRM. They need someone making sure the CRM is being worked.
          </Reveal>
          <Reveal as="div" delay={280} className="mt-8 inline-block">
            <div className="h-display text-[42px] sm:text-[64px] grad-text leading-none">That&rsquo;s VSM.</div>
          </Reveal>
        </div>
      </section>

      {/* ═════════ SECTION 3 — FOUR-STAGE MODEL ═════════ */}
      <section id="how-vsm-works" className="py-20 lg:py-28 bg-bp-slate-50 scroll-mt-24">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>The model</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[52px]">Sales Management Doesn&rsquo;t Need to Be <span className="grad-text">Complicated.</span></h2>
            <p className="mt-5 text-[17px] text-bp-slate">Every contact is managed through one of four simple stages.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {FOUR_STAGES.map((s, i) => (
              <Reveal as="div" key={s.tag} delay={i * 90} className="relative bg-white rounded-3xl border border-bp-slate-100 p-6 lg:p-7 shadow-sm hover:shadow-xl transition-all group">
                <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-white bg-${s.color}`}>{s.tag}</div>
                <div className="font-display font-extrabold text-[36px] leading-none mt-4 grad-text">{s.n}</div>
                <div className="font-display font-bold text-[19px] text-bp-ink mt-3">{s.title}</div>
                <p className="text-[14px] text-bp-slate mt-2.5 leading-relaxed">{s.body}</p>
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {s.chips.map((c) => (
                    <span key={c} className="text-[11px] font-semibold text-bp-slate bg-bp-slate-100 rounded-full px-2.5 py-1">{c}</span>
                  ))}
                </div>
                {i < FOUR_STAGES.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-bp-slate-soft" size={18}/>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 4 — HOW VSM WORKS FLOW ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>The flow</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">We Work the Opportunities Your <span className="grad-text">Marketing Generates.</span></h2>
          </div>
          <div className="mt-14 space-y-4">
            {[
              { label: 'MARKETING', items: 'SEO · Google Ads · Website · Social · Referral', bg: 'bg-bp-slate-50', tone: 'text-bp-slate' },
              { label: 'LEAD',      items: 'Calls + Texts + Emails + Remarketing',              bg: 'bg-bp-blue-50', tone: 'text-bp-blue' },
              { label: 'PROSPECT',  items: 'Calls + Texts + Emails + Remarketing + Mail',       bg: 'bg-bp-teal/10', tone: 'text-bp-teal' },
            ].map((r, i) => (
              <div key={r.label} className="relative">
                <Reveal delay={i * 80} className={`${r.bg} border border-bp-slate-100 rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`}>
                  <div className={`font-display font-black text-[15px] uppercase tracking-[0.18em] ${r.tone}`}>{r.label}</div>
                  <div className="text-[14.5px] text-bp-slate">{r.items}</div>
                </Reveal>
                <div className="flex justify-center py-1"><ArrowDown size={18} className="text-bp-slate-soft"/></div>
              </div>
            ))}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl p-6 bg-gradient-to-br from-bp-teal to-[#0da78e] text-white shadow-lg">
                <div className="font-display font-black text-[16px] uppercase tracking-[0.18em]">DEAL</div>
                <div className="mt-2 text-[14.5px]">Enters the Referral Program</div>
              </div>
              <div className="rounded-2xl p-6 bg-bp-slate-100 text-bp-slate">
                <div className="font-display font-black text-[16px] uppercase tracking-[0.18em] text-bp-ink">LOST</div>
                <div className="mt-2 text-[14.5px]">No further action. Simple, clean, measurable.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 5 — WHAT WE DO ═════════ */}
      <section className="py-20 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Execution</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Consistent Sales Follow-Up. <span className="grad-text">Without adding another full-time manager.</span></h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_WE_DO.map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal as="article" key={d.title} delay={i * 60} className="card-soft p-6 lg:p-7 group">
                  <div className="w-12 h-12 rounded-2xl bg-bp-blue-50 text-bp-blue flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Icon size={22}/>
                  </div>
                  <h3 className="font-display font-bold text-[19px] text-bp-ink mt-5">{d.title}</h3>
                  <p className="text-[14.5px] text-bp-slate mt-2.5 leading-relaxed">{d.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 6 — AI + HUMAN MODEL ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Operating model</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Automation Where It Makes Sense.<br/><span className="grad-text">Humans Where They Matter.</span></h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {OPERATING_MODEL.map((o, i) => {
              const Icon = o.icon;
              return (
                <Reveal as="div" key={o.pill} delay={i * 70} className={`rounded-3xl p-6 lg:p-7 text-white bg-gradient-to-br ${o.bg} shadow-lg`}>
                  <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
                    <Icon size={13}/> {o.pill}
                  </div>
                  <div className="mt-5 font-display font-bold text-[15px] uppercase tracking-wider opacity-85">Handles</div>
                  <ul className="mt-3 space-y-1.5 text-[14px]">
                    {o.items.map((it) => (<li key={it} className="flex items-start gap-2"><span className="w-1 h-1 bg-white/70 rounded-full mt-2 flex-shrink-0"/>{it}</li>))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-14 text-center">
            <p className="font-display font-extrabold text-[22px] sm:text-[28px] text-bp-ink max-w-2xl mx-auto leading-snug">
              Your salespeople sell.<br/><span className="grad-text">VSM helps make sure they have someone to sell to.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 7 — VSM DASHBOARD (Big) ═════════ */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-bp-ink via-[#111827] to-[#0a1224] text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage:'radial-gradient(circle at 25% 25%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 65%, #fff 1px, transparent 1px)', backgroundSize:'50px 50px, 70px 70px' }}/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative">
          <div className="max-w-3xl">
            <div className="kicker text-bp-teal"><span className="dot" style={{background:'#14B8A6'}}/>The dashboard</div>
            <h2 className="h-display text-white mt-3 text-[36px] sm:text-[54px]">See Exactly What <span className="text-[#8DD9CB]">We&rsquo;re Doing.</span></h2>
            <p className="mt-5 text-[16.5px] text-white/85 max-w-xl">Every VSM client receives a live performance dashboard. Not a monthly PDF report - a live view of the sales work being done on your behalf.</p>
          </div>

          {/* Date range strip */}
          <div className="mt-10 flex flex-wrap gap-2 text-[12px] font-semibold">
            {['Today','Last 7 Days','Last 30 Days','This Month','Last Month','Year to Date','Custom'].map((r, i) => (
              <span key={r} className={`px-3 py-1.5 rounded-full ${i===2 ? 'bg-white text-bp-ink' : 'bg-white/10 border border-white/15 text-white/85'}`}>{r}</span>
            ))}
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-5">
            {/* Activity */}
            <div className="bg-white text-bp-ink rounded-3xl p-6 shadow-xl">
              <div className="flex items-center gap-2 text-bp-blue"><Zap size={16}/><span className="text-[11.5px] font-bold uppercase tracking-widest">VSM Activity</span></div>
              <div className="mt-4">
                <ActivityRow label="Calls"   value="326"/>
                <ActivityRow label="Texts"   value="418"/>
                <ActivityRow label="Emails"  value="287"/>
                <ActivityRow label="Mailers" value="43"/>
              </div>
              <div className="mt-4 pt-4 border-t border-bp-slate-100 flex items-center justify-between">
                <span className="text-[11.5px] font-bold uppercase tracking-wider text-bp-slate-soft">Total Activities</span>
                <span className="font-display font-black text-[22px] grad-text">1,074</span>
              </div>
            </div>
            {/* Pipeline */}
            <div className="bg-white text-bp-ink rounded-3xl p-6 shadow-xl">
              <div className="flex items-center gap-2 text-bp-teal"><Target size={16}/><span className="text-[11.5px] font-bold uppercase tracking-widest">Sales Pipeline</span></div>
              <div className="mt-4">
                <ActivityRow label="Leads"      value="124"/>
                <ActivityRow label="Prospects"  value="47"/>
                <ActivityRow label="Deals"      value="14"/>
                <ActivityRow label="Lost"       value="30"/>
              </div>
              <div className="mt-4 pt-4 border-t border-bp-slate-100 flex items-center justify-between">
                <span className="text-[11.5px] font-bold uppercase tracking-wider text-bp-slate-soft">Total Contacts</span>
                <span className="font-display font-black text-[22px] text-bp-teal">215</span>
              </div>
            </div>
            {/* Results */}
            <div className="bg-white text-bp-ink rounded-3xl p-6 shadow-xl">
              <div className="flex items-center gap-2 text-[#F59E0B]"><Trophy size={16}/><span className="text-[11.5px] font-bold uppercase tracking-widest">Sales Results</span></div>
              <div className="mt-4 space-y-3">
                <div><div className="text-[11px] uppercase tracking-wider text-bp-slate-soft font-bold">Signed Deals</div><div className="font-display font-black text-[24px] text-bp-blue">11</div></div>
                <div><div className="text-[11px] uppercase tracking-wider text-bp-slate-soft font-bold">Average Job Size</div><div className="font-display font-black text-[24px] text-bp-ink">$29,773</div></div>
                <div className="pt-3 border-t border-bp-slate-100"><div className="text-[11px] uppercase tracking-wider text-bp-slate-soft font-bold">Signed Deal Value</div><div className="font-display font-black text-[32px] grad-text mt-1">$327,500</div></div>
              </div>
            </div>
          </div>

          {/* Referrals strip */}
          <div className="mt-5 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/15 p-6 grid grid-cols-3 gap-6 text-white">
            <div><div className="text-[10.5px] uppercase tracking-wider font-bold text-white/70">Referral Requests</div><div className="font-display font-black text-[26px] mt-1">24</div></div>
            <div><div className="text-[10.5px] uppercase tracking-wider font-bold text-white/70">Referrals Generated</div><div className="font-display font-black text-[26px] text-[#8DD9CB] mt-1">8</div></div>
            <div><div className="text-[10.5px] uppercase tracking-wider font-bold text-white/70">Referral Deals</div><div className="font-display font-black text-[26px] text-[#8DD9CB] mt-1">3</div></div>
          </div>

          <div className="mt-8 text-center text-[11.5px] text-white/60 italic">Sample dashboard data for illustration.</div>
        </div>
      </section>

      {/* ═════════ SECTION 8 — PROVE THE WORK ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Prove the work</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Know Exactly What VSM Is <span className="grad-text">Doing For Your Business.</span></h2>
            <p className="mt-5 text-[17px] text-bp-slate">Traditional sales-management services can be difficult to quantify. VSM is designed differently - every major activity is tracked.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { n: '326', label: 'Calls',   accent: 'text-bp-blue' },
              { n: '418', label: 'Texts',   accent: 'text-bp-teal' },
              { n: '287', label: 'Emails',  accent: 'text-[#F59E0B]' },
              { n: '43',  label: 'Mailers', accent: 'text-[#FB6A48]' },
            ].map((s, i) => (
              <Reveal key={s.label} delay={i * 70} className="card-soft p-6 lg:p-7 text-center">
                <div className={`font-display font-black text-[52px] leading-none ${s.accent}`}>{s.n}</div>
                <div className="mt-3 text-[12px] uppercase tracking-widest font-bold text-bp-slate-soft">{s.label}</div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-3 bg-bp-blue-50 border border-bp-blue/20 rounded-full px-6 py-3">
              <TrendingUp size={18} className="text-bp-blue"/>
              <span className="font-display font-black text-[20px] sm:text-[24px] text-bp-ink">1,074 Sales Activities Completed</span>
            </div>
            <p className="mt-4 text-[14px] text-bp-slate-soft max-w-xl mx-auto">You&rsquo;ll be able to see exactly how actively your database is being worked.</p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 9 — PROVE THE RESULTS ═════════ */}
      <section className="py-20 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Prove the results</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Activity Is Good. <span className="grad-text">Revenue Is Better.</span></h2>
            <p className="mt-5 text-[17px] text-bp-slate">VSM tracks both effort and results - so you can see the connection between the work and the revenue it produces.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { label: 'Signed Deals',      value: '11',       sub: 'This period' },
              { label: 'Average Job Size',  value: '$29,773',  sub: 'Historical' },
              { label: 'Signed Deal Value', value: '$327,500', sub: 'This period', hero: true },
            ].map((r) => (
              <div key={r.label} className={`rounded-3xl p-7 ${r.hero ? 'bg-gradient-to-br from-bp-blue via-[#0a5fd1] to-bp-teal text-white shadow-xl' : 'bg-white border border-bp-slate-100 shadow-sm'}`}>
                <div className={`text-[11.5px] font-bold uppercase tracking-widest ${r.hero ? 'text-white/80' : 'text-bp-slate-soft'}`}>{r.label}</div>
                <div className={`font-display font-black text-[40px] sm:text-[48px] mt-2 leading-none ${r.hero ? 'text-white' : 'grad-text'}`}>{r.value}</div>
                <div className={`text-[12px] mt-2 ${r.hero ? 'text-white/70' : 'text-bp-slate-soft'}`}>{r.sub}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 max-w-3xl mx-auto text-center">
            <p className="text-[14.5px] text-bp-slate">Where actual deal values are available, VSM reports actual signed revenue. Where the actual value isn&rsquo;t available, estimated revenue may be calculated using the client&rsquo;s average job size.</p>
            <p className="mt-3 text-[12px] text-bp-slate-soft italic">Revenue reporting may include estimated values when actual transaction amounts are unavailable.</p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 10 — INFLUENCED REVENUE ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Attribution</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">See Which Deals <span className="grad-text">VSM Helped Influence.</span></h2>
            <p className="mt-5 text-[16.5px] text-bp-slate">A deal is classified as VSM Influenced when VSM performed meaningful outreach - a call, text, email, mailer or campaign - before the opportunity became a customer.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <div className="card-soft p-7 text-center">
              <div className="text-[11.5px] font-bold uppercase tracking-widest text-bp-slate-soft">VSM Influenced Deals</div>
              <div className="font-display font-black text-[52px] text-bp-blue mt-2 leading-none">9</div>
            </div>
            <div className="card-soft p-7 text-center">
              <div className="text-[11.5px] font-bold uppercase tracking-widest text-bp-slate-soft">Influenced Deal Value</div>
              <div className="font-display font-black text-[52px] grad-text mt-2 leading-none">$273,500</div>
            </div>
            <div className="card-soft p-7 text-center">
              <div className="text-[11.5px] font-bold uppercase tracking-widest text-bp-slate-soft">Influence Rate</div>
              <div className="font-display font-black text-[52px] text-bp-teal mt-2 leading-none">81.8%</div>
            </div>
          </div>
          <p className="mt-8 text-[13px] text-bp-slate-soft italic text-center max-w-2xl mx-auto">Influence connects VSM&rsquo;s work directly to sales outcomes without claiming sole causation.</p>
        </div>
      </section>

      {/* ═════════ SECTION 11 — FUNNEL MOVEMENT ═════════ */}
      <section className="py-20 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Funnel movement</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Don&rsquo;t Just Count Leads.<br/><span className="grad-text">Measure Movement.</span></h2>
            <p className="mt-5 text-[16.5px] text-bp-slate">VSM doesn&rsquo;t just show how many names are sitting inside a database. We measure how opportunities progress.</p>
          </div>
          <div className="mt-14 max-w-2xl mx-auto space-y-3">
            {[
              { n: 83, label: 'New Leads',        color: 'from-bp-blue to-[#0a5fd1]', pct: 100 },
              { n: 31, label: 'Became Prospects', color: 'from-bp-teal to-[#0da78e]', pct: 37 },
              { n: 11, label: 'Became Deals',     color: 'from-[#F59E0B] to-[#d97706]', pct: 13 },
              { n: 14, label: 'Moved to Lost',    color: 'from-bp-slate to-[#4b5563]', pct: 17 },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-bp-slate-100 p-4 flex items-center gap-4">
                <div className={`font-display font-black text-[34px] w-16 text-center bg-gradient-to-br ${s.color} bg-clip-text text-transparent`}>{s.n}</div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-bp-ink">{s.label}</div>
                  <div className="mt-1.5 h-2 bg-bp-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${s.color}`} style={{ width: `${s.pct}%` }}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 12 — REFERRAL PROGRAM ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="kicker"><span className="dot"/>Referral program</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">The Sale Doesn&rsquo;t Have to Be <span className="grad-text">the End.</span></h2>
            <p className="mt-5 text-[16.5px] text-bp-slate leading-relaxed">Once a prospect becomes a customer, VSM can move them into a structured referral program - creating a compounding source of new opportunities.</p>
            <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-6 text-[14px] text-bp-slate">
              {['Customers enrolled','Referral requests','Referrals generated','Referral deals','Referral revenue'].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-bp-teal mt-0.5 flex-shrink-0"/>{t}</li>
              ))}
            </ul>
            <p className="mt-6 font-display font-extrabold text-[18px] text-bp-ink">Turn today&rsquo;s customer into <span className="grad-text">tomorrow&rsquo;s lead source.</span></p>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl bg-gradient-to-br from-bp-blue-50 to-white p-8 border border-bp-blue/20 space-y-3">
              {[
                { l: 'DEAL',              c: 'bg-bp-teal text-white' },
                { l: 'Referral Request',  c: 'bg-white border border-bp-slate-200 text-bp-ink' },
                { l: 'Referral Follow-Up',c: 'bg-white border border-bp-slate-200 text-bp-ink' },
                { l: 'New Referral',      c: 'bg-white border border-bp-slate-200 text-bp-ink' },
                { l: 'NEW LEAD',          c: 'bg-bp-blue text-white' },
              ].map((s, i) => (
                <div key={s.l}>
                  <div className={`rounded-2xl px-5 py-4 font-display font-bold text-[14.5px] uppercase tracking-widest ${s.c} text-center shadow-sm`}>{s.l}</div>
                  {i < 4 && <div className="flex justify-center py-1"><ArrowDown size={14} className="text-bp-slate-soft"/></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 13-14 — CRM ═════════ */}
      <section className="py-20 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Your workspace</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Your Entire VSM Pipeline<br/><span className="grad-text">In One Place.</span></h2>
            <p className="mt-5 text-[16.5px] text-bp-slate">Every VSM client gets a dedicated CRM workspace. Four stages. One clear sales process.</p>
          </div>

          <div className="mt-12 rounded-3xl bg-white border border-bp-slate-200 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 lg:px-6 py-4 border-b border-bp-slate-100">
              <div className="flex items-center gap-2">
                <LayoutDashboard className="text-bp-blue" size={18}/>
                <span className="font-display font-bold text-bp-ink text-[16px]">Contacts</span>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <div className="flex items-center gap-2 bg-bp-slate-50 rounded-lg px-3 py-1.5 text-[12.5px] text-bp-slate-soft border border-bp-slate-100">
                  <Search size={13}/> Search contacts
                </div>
                <div className="text-[12px] text-bp-slate-soft">Last 30 Days</div>
              </div>
            </div>
            <div className="flex items-center gap-1 px-5 lg:px-6 border-b border-bp-slate-100 overflow-x-auto">
              {['ALL','LEAD','PROSPECT','DEAL','LOST'].map((t, i) => (
                <span key={t} className={`px-3 py-3 text-[11.5px] font-bold uppercase tracking-widest border-b-2 -mb-px whitespace-nowrap ${i===0 ? 'border-bp-blue text-bp-blue' : 'border-transparent text-bp-slate-soft'}`}>{t}</span>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]" data-testid="vsm-crm-table">
                <thead className="bg-bp-slate-50 text-bp-slate-soft">
                  <tr>
                    {['Contact','Stage','Last Activity','Activities','Value',''].map((h) => (
                      <th key={h} className="text-left px-5 py-3 font-bold uppercase text-[10.5px] tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Robert Smith',    stage: 'PROSPECT', stageColor: 'bg-bp-teal/15 text-bp-teal',      activity: 'Call — Today',       count: 11, value: '$42,500' },
                    { name: 'Jennifer Lee',    stage: 'PROSPECT', stageColor: 'bg-bp-teal/15 text-bp-teal',      activity: 'Text — Today',       count: 8,  value: '$31,000' },
                    { name: 'Michael Jones',   stage: 'LEAD',     stageColor: 'bg-bp-blue-50 text-bp-blue',      activity: 'Email — 1 hr ago',   count: 3,  value: '—' },
                    { name: 'Sarah Davis',     stage: 'DEAL',     stageColor: 'bg-[#F59E0B]/15 text-[#d97706]',  activity: 'Referral Program',   count: 14, value: '$38,700' },
                  ].map((r) => (
                    <tr key={r.name} className="border-t border-bp-slate-100 hover:bg-bp-slate-50 transition-colors">
                      <td className="px-5 py-4 font-semibold text-bp-ink">{r.name}</td>
                      <td className="px-5 py-4"><span className={`inline-block rounded-full px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-widest ${r.stageColor}`}>{r.stage}</span></td>
                      <td className="px-5 py-4 text-bp-slate">{r.activity}</td>
                      <td className="px-5 py-4 font-mono text-bp-ink">{r.count}</td>
                      <td className="px-5 py-4 font-semibold text-bp-ink">{r.value}</td>
                      <td className="px-5 py-4 text-right"><span className="text-bp-blue text-[13px] font-semibold">View</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-6 text-[12px] text-bp-slate-soft italic text-center">Sample data. Your actual dashboard reflects your real pipeline in real time.</p>
        </div>
      </section>

      {/* ═════════ SECTION 15 — CLIENT VISIBILITY ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8 text-center">
          <div className="kicker justify-center"><span className="dot"/>Visibility</div>
          <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Full Visibility. <span className="grad-text">Without Micromanaging Us.</span></h2>
          <p className="mt-6 text-[17px] text-bp-slate max-w-2xl mx-auto leading-relaxed">Our job is to manage the process. Your job shouldn&rsquo;t be approving every text, email or phone script. VSM operates within agreed guidelines and gives you visibility into:</p>
          <div className="mt-10 grid sm:grid-cols-2 gap-3 text-left max-w-xl mx-auto">
            {['What was done','Who was contacted','How they were contacted','Where they sit in the pipeline','Which opportunities converted','How much revenue was signed'].map((v) => (
              <div key={v} className="flex items-start gap-2.5 text-[15px] text-bp-slate"><CheckCircle2 size={17} className="text-bp-teal mt-0.5 flex-shrink-0"/>{v}</div>
            ))}
          </div>
          <p className="mt-10 font-display font-extrabold text-[22px] sm:text-[28px] text-bp-ink">We execute. <span className="grad-text">You see the results.</span></p>
        </div>
      </section>

      {/* ═════════ SECTION 16 — WHY VSM ═════════ */}
      <section className="py-20 lg:py-24 bg-bp-slate-50">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Why VSM</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Why Outsource <span className="grad-text">Sales Management?</span></h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_VSM.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal as="article" key={w.title} delay={i * 60} className="card-soft p-7">
                  <div className="w-12 h-12 rounded-2xl bg-bp-teal-50 text-bp-teal flex items-center justify-center"><Icon size={22}/></div>
                  <h3 className="font-display font-bold text-[20px] text-bp-ink mt-5">{w.title}</h3>
                  <p className="text-[14.5px] text-bp-slate mt-2.5 leading-relaxed">{w.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 17 — INDUSTRIES ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Built for</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">Built For Businesses Where <span className="grad-text">One Sale Matters.</span></h2>
            <p className="mt-5 text-[16.5px] text-bp-slate">VSM is especially valuable when one additional closed project can mean thousands - or tens of thousands - in revenue.</p>
          </div>
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind) => {
              const Icon = ind.icon;
              return (
                <div key={ind.name} className="card-soft p-5 flex items-center gap-3 hover:border-bp-blue transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-bp-blue-50 text-bp-blue flex items-center justify-center flex-shrink-0"><Icon size={18}/></div>
                  <div className="text-[14.5px] font-semibold text-bp-ink leading-tight">{ind.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 18 — ROI ═════════ */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-bp-blue via-[#0a5fd1] to-bp-teal text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'radial-gradient(circle at 15% 30%, #fff 1px, transparent 1px), radial-gradient(circle at 85% 70%, #fff 1px, transparent 1px)', backgroundSize: '50px 50px, 70px 70px' }}/>
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-3 py-1"><LineChart size={13}/><span className="text-[11px] font-bold uppercase tracking-widest">ROI Example</span></div>
            <h2 className="h-display text-white mt-5 text-[36px] sm:text-[48px]">One Additional Deal Can <span className="text-[#8DD9CB]">Change the Math.</span></h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6"><div className="text-[11.5px] uppercase tracking-widest font-bold text-white/80">Average Job Size</div><div className="font-display font-black text-[38px] mt-2">$30,000</div></div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6"><div className="text-[11.5px] uppercase tracking-widest font-bold text-white/80">VSM Investment</div><div className="font-display font-black text-[38px] mt-2">$3,000<span className="text-[16px] text-white/70">/mo</span></div></div>
          </div>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { d: '1 Deal',   v: '$30,000' },
              { d: '3 Deals',  v: '$90,000' },
              { d: '5 Deals',  v: '$150,000' },
              { d: '10 Deals', v: '$300,000' },
            ].map((r) => (
              <div key={r.d} className="bg-white text-bp-ink rounded-2xl p-5 text-center">
                <div className="text-[11px] font-bold uppercase tracking-widest text-bp-slate-soft">{r.d}</div>
                <div className="font-display font-black text-[24px] grad-text mt-1.5">{r.v}</div>
                <div className="text-[10.5px] text-bp-slate-soft mt-0.5">signed revenue</div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-[12px] text-white/70 italic text-center max-w-xl mx-auto">Illustrative example only. VSM does not guarantee any specific number of deals, revenue, conversion rate or return.</p>
        </div>
      </section>

      {/* ═════════ SECTION 19 — BRAND PARTNERS CONNECTION ═════════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Brand Partners + VSM</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">We Generate Opportunities.<br/><span className="grad-text">Now We Help Manage What Happens Next.</span></h2>
            <p className="mt-6 text-[16.5px] text-bp-slate leading-relaxed">Brand Partners has 19 years of experience generating leads through digital marketing. VSM expands that partnership beyond marketing - into sales management, follow-up and revenue.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { l: 'ATTRACT',   sub: 'SEO · Ads · Web · Social',      c: 'bg-bp-blue-50 text-bp-blue' },
              { l: 'GENERATE',  sub: 'Lead',                          c: 'bg-bp-teal/15 text-bp-teal' },
              { l: 'VSM',       sub: 'Follow-up · Sales · Activity',  c: 'bg-gradient-to-br from-bp-blue to-bp-teal text-white shadow-lg' },
              { l: 'CLOSE',     sub: 'Deal',                          c: 'bg-[#F59E0B]/15 text-[#d97706]' },
              { l: 'GROW',      sub: 'Referral',                      c: 'bg-[#FB6A48]/15 text-[#e94a26]' },
            ].map((s) => (
              <div key={s.l} className={`rounded-2xl p-5 text-center ${s.c}`}>
                <div className="font-display font-black text-[14px] uppercase tracking-widest">{s.l}</div>
                <div className="mt-2 text-[12px] font-semibold opacity-90">{s.sub}</div>
              </div>
            ))}
          </div>
          <p className="mt-10 font-display font-extrabold text-[22px] sm:text-[28px] text-bp-ink text-center">From marketing to <span className="grad-text">signed business.</span></p>
        </div>
      </section>

      {/* ═════════ SECTION 20 — FINAL CTA + FORM ═════════ */}
      <section id="vsm-form" className="py-20 lg:py-24 bg-bp-slate-50 border-t border-bp-slate-100 scroll-mt-24">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="kicker"><span className="dot"/>Let&rsquo;s talk</div>
            <h2 className="h-display mt-3 text-[36px] sm:text-[48px]">How Many Opportunities Are Sitting <span className="grad-text">In Your Database Right Now?</span></h2>
            <p className="mt-5 text-[17px] text-bp-slate leading-relaxed">Let&rsquo;s look at your current sales process, lead database and follow-up strategy - and identify where VSM may be able to help you close more business.</p>
            <div className="mt-8 space-y-3 text-[14px]">
              <a href={`tel:${CONTACT_INFO.phoneTel}`} onClick={trackCTA('final-phone')} className="inline-flex items-center gap-2.5 text-bp-slate hover:text-bp-blue" data-testid="vsm-phone-cta">
                <Phone size={16} className="text-bp-blue"/> <span className="font-semibold">{CONTACT_INFO.phone}</span>
              </a>
              <br/>
              <a href={`mailto:${CONTACT_INFO.email}`} onClick={trackCTA('final-email')} className="inline-flex items-center gap-2.5 text-bp-slate hover:text-bp-blue" data-testid="vsm-email-cta">
                <Mail size={16} className="text-bp-blue"/> <span className="font-semibold">{CONTACT_INFO.email}</span>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" onClick={trackCTA('final-secondary')} className="btn-secondary" data-testid="vsm-secondary-cta">
                Talk to Brand Partners <ArrowRight size={15}/>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="rounded-3xl bg-white border border-bp-slate-200 shadow-2xl p-6 lg:p-8"
              data-testid="vsm-form"
              onFocus={() => { if (window.gtag) window.gtag('event', 'vsm_form_start'); }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-bp-blue-50 text-bp-blue flex items-center justify-center"><Target size={20}/></div>
                <div>
                  <div className="font-display font-bold text-bp-ink text-[17px]">Let&rsquo;s Review Your Sales Process</div>
                  <div className="text-[12.5px] text-bp-slate-soft">Response within one business day</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label htmlFor="v-fn" className="bp-label">First Name *</label><input id="v-fn" required className="bp-field" value={form.firstName} onChange={update('firstName')} placeholder="Jane"/></div>
                <div><label htmlFor="v-ln" className="bp-label">Last Name</label><input id="v-ln" className="bp-field" value={form.lastName} onChange={update('lastName')} placeholder="Smith"/></div>
                <div><label htmlFor="v-co" className="bp-label">Company *</label><input id="v-co" required className="bp-field" value={form.company} onChange={update('company')} placeholder="Acme Services"/></div>
                <div><label htmlFor="v-web" className="bp-label">Website</label><input id="v-web" className="bp-field" value={form.website} onChange={update('website')} placeholder="acme.com"/></div>
                <div><label htmlFor="v-em" className="bp-label">Email *</label><input id="v-em" required type="email" className="bp-field" value={form.email} onChange={update('email')} placeholder="jane@acme.com"/></div>
                <div><label htmlFor="v-ph" className="bp-label">Phone</label><input id="v-ph" type="tel" className="bp-field" value={form.phone} onChange={update('phone')} placeholder="(555) 123-4567"/></div>
                <div className="sm:col-span-2"><label htmlFor="v-ind" className="bp-label">Industry</label><input id="v-ind" className="bp-field" value={form.industry} onChange={update('industry')} placeholder="e.g. HVAC, Pools, Roofing"/></div>
                <div><label htmlFor="v-mlv" className="bp-label">Monthly Lead Volume</label><input id="v-mlv" className="bp-field" value={form.monthlyLeadVolume} onChange={update('monthlyLeadVolume')} placeholder="~ per month"/></div>
                <div><label htmlFor="v-ajs" className="bp-label">Average Job Size</label><input id="v-ajs" className="bp-field" value={form.avgJobSize} onChange={update('avgJobSize')} placeholder="e.g. $25,000"/></div>
                <div><label htmlFor="v-sp" className="bp-label">Number of Salespeople</label><input id="v-sp" className="bp-field" value={form.salespeople} onChange={update('salespeople')} placeholder="e.g. 3"/></div>
                <div><label htmlFor="v-crm" className="bp-label">Current CRM</label><input id="v-crm" className="bp-field" value={form.currentCrm} onChange={update('currentCrm')} placeholder="e.g. HubSpot, JobNimbus, None"/></div>
                <div className="sm:col-span-2"><label htmlFor="v-exl" className="bp-label">Existing Leads / Prospects (approx.)</label><input id="v-exl" className="bp-field" value={form.existingLeads} onChange={update('existingLeads')} placeholder="How many contacts in your database?"/></div>
                <div className="sm:col-span-2">
                  <label htmlFor="v-ch" className="bp-label">Biggest Sales Challenge</label>
                  <div className="relative">
                    <select id="v-ch" className="bp-field appearance-none pr-9" value={form.challenge} onChange={update('challenge')}>
                      <option value="">Select one</option>
                      {CHALLENGES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-bp-slate-soft pointer-events-none" size={16}/>
                  </div>
                </div>
              </div>
              <button type="submit" disabled={status.state === 'loading'} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="vsm-submit">
                {status.state === 'loading' ? 'Sending…' : <>Schedule My VSM Review <ArrowRight size={16}/></>}
              </button>
              {status.state === 'success' && (
                <div className="mt-4 flex items-start gap-3 p-3.5 rounded-xl bg-bp-teal-50 border border-bp-teal/30" data-testid="vsm-form-success">
                  <CheckCircle2 size={18} className="text-bp-teal mt-0.5 flex-shrink-0"/>
                  <div className="text-[13.5px] text-bp-slate">{status.message}</div>
                </div>
              )}
              {status.state === 'error' && (
                <div className="mt-4 flex items-start gap-3 p-3.5 rounded-xl bg-red-50 border border-red-200" data-testid="vsm-form-error">
                  <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0"/>
                  <div className="text-[13.5px] text-bp-ink">{status.message}</div>
                </div>
              )}
              <p className="mt-4 text-[11.5px] text-bp-slate-soft text-center">No pressure. No spam. Just an honest look at how VSM might work for your business.</p>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
